/**
 * VenueWorkshopsTab — the Workshops tab body (BON-168 / BON-197). A 6-KPI bar
 * (Past / Future / Total Tickets Sold / Avg Fill / Venue Since / Cadence) over
 * the "Workshops here" table, with an `Upcoming | Past | All` segmented
 * toggle top-right of the card. Data comes from `useVenueWorkshopStats`
 * + `useVenueWorkshopsList`, which call the live `/stats` endpoint and the
 * `/workshops` list (enriched with `paceStatus`/`opFill`/`crew`). Selecting a
 * direction swaps the rows in the same table.
 */
import { useState } from 'react';

import type {
  VenueCadence,
  VenueWorkshopStats,
  WorkshopDirection,
} from '../../../../api/workshops';
import { usePermissions } from '../../../../hooks/usePermissions';
import { Permission } from '../../../../types/permissions';
import { KpiStrip, type KpiCell } from '../../primitives';
import { useWritableRegions } from '../../primitives/use-writable-regions';
import type { WorkshopShell } from '../../workshopDetailUrlFor';
import CreateWorkshopDrawer from '../../workshops/create/CreateWorkshopDrawer';
import { useVenue } from '../useVenue';

import { useVenueWorkshopsList, useVenueWorkshopStats } from './useVenueWorkshops';
import { WorkshopsCardBody } from './WorkshopsCardBody';
import { WorkshopsCardHeader } from './WorkshopsCardHeader';

const EM_DASH = '—';

function venueSinceLabel(iso: string | null): string {
  if (!iso) return EM_DASH;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

/** Avg fill is null when the venue has no past workshops with capacity — show
 *  an em-dash rather than "null%". */
function avgFillLabel(avgFill: number | null): string {
  if (avgFill === null) return EM_DASH;
  return `${avgFill}%`;
}

/** The recurring cadence the venue runs on, or an em-dash when no series has
 *  a workshop still to come. "Scheduled" counts drafts as well as published
 *  workshops, unlike Future Workshops beside it, which counts only what is on
 *  sale. */
function cadenceKpi(cadence: VenueCadence | null | undefined): KpiCell {
  if (!cadence) return { label: 'Cadence', value: EM_DASH };
  const more = cadence.activeSeries > 1 ? ` · +${cadence.activeSeries - 1} more` : '';
  return {
    label: 'Cadence',
    value: cadence.headline,
    sub: `${cadence.detail} · ${cadence.remainingWorkshops} scheduled${more}`,
  };
}

function statsToKpis(stats: VenueWorkshopStats): KpiCell[] {
  return [
    { label: 'Past Workshops', value: String(stats.pastWorkshops) },
    { label: 'Future Workshops', value: String(stats.futureWorkshops) },
    { label: 'Total Tickets Sold', value: stats.totalTicketsSold.toLocaleString('en-US') },
    { label: 'Avg Fill', value: avgFillLabel(stats.avgFill) },
    { label: 'Venue Since', value: venueSinceLabel(stats.venueSince) },
    cadenceKpi(stats.cadence),
  ];
}

/**
 * Whether to draw the "+ New workshop" trigger. Without the capability the
 * create call can only 403, so the button would be a live-looking control that
 * never works. Withheld while the token is still decoding too, since
 * `hasPermission` answers false until then and the button would otherwise
 * appear a beat after the page.
 */
function useCanCreateWorkshop(): boolean {
  const { hasPermission, loading } = usePermissions();
  return !loading && hasPermission(Permission.WRITE_WORKSHOPS);
}

export function VenueWorkshopsTab({
  venueId,
  shell,
}: {
  venueId: string;
  /** Dashboards shell — drives the post-create redirect. */
  shell: WorkshopShell;
}) {
  const [direction, setDirection] = useState<WorkshopDirection>('upcoming');
  const [showCancelled, setShowCancelled] = useState(false);
  const statsQuery = useVenueWorkshopStats(venueId);
  const listQuery = useVenueWorkshopsList(venueId, direction, showCancelled);
  const venue = useVenue(venueId).data;
  const venueName = venue?.name ?? 'this venue';
  // The writable-regions tree lets the drawer resolve the fixed region's name
  // (the venue carries only `regionId`).
  const { regions } = useWritableRegions();
  const canCreate = useCanCreateWorkshop();
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
      {statsQuery.data && <KpiStrip items={statsToKpis(statsQuery.data)} />}

      <section className="nb-card" style={{ minHeight: 0 }}>
        <WorkshopsCardHeader
          venueName={venueName}
          canCreate={canCreate}
          direction={direction}
          onDirectionChange={setDirection}
          showCancelled={showCancelled}
          onToggleCancelled={() => setShowCancelled((v) => !v)}
          onCreate={() => setShowCreate(true)}
        />
        <WorkshopsCardBody query={listQuery} direction={direction} />
      </section>

      {showCreate && (
        <CreateWorkshopDrawer
          shell={shell}
          regionId={venue?.regionId ?? null}
          regionMode="fixed"
          regions={regions}
          venueId={venueId}
          venueMode="fixed"
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  );
}
